import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, ComponentFactoryResolver, ComponentRef, Inject, OnDestroy, OnInit, ReflectiveInjector, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, ReplaySubject, Subscription } from 'rxjs';
import { Offer } from 'src/app/classes';
import { Guid } from 'src/app/globals';
import { MailService } from 'src/app/lib/mail/services/mail.service';
import { CalendarService } from 'src/app/lib/nextcloud/services/calendar.service';
import { PersistenceService, ServerCommand } from 'src/app/lib/shared/services/persistence.service';
import { AppStateService } from 'src/app/services/app-state.service';
import { FileService } from 'src/app/services/file.service';
import { TransactionSummaryFormComponent } from '../../../../issue/components/embedded/transaction-summary-form/transaction-summary-form.component';
import { OfferService } from '../../../services/offer.service';
import { OfferTransaction } from '../../../classes';
import { IOfferSummaryView, IOfferTransactionView } from '../../../interfaces';
import { OfferTransactionMap } from '../../../globals';

@Component({
  selector: 'app-offer-transaction-dialog',
  templateUrl: './offer-transaction-dialog.component.html',
  styleUrls: ['./offer-transaction-dialog.component.css']
})
export class OfferTransactionDialogComponent implements OnDestroy, OnInit {

  steps: Step[] = [];
  visibleSteps: Step[] = [];

  stepperIndex: number = 0;

  hasPreviousStep: boolean = false;
  hasNextStep: boolean = false;

  formGroup: FormGroup = new FormGroup({

  });
  title: string = "Transaktion ausführen";

  componentRef: ComponentRef<IOfferTransactionView>;

  _innerHost = new ReplaySubject<ViewContainerRef>();
  innerHost$ = this._innerHost.asObservable();
  @ViewChild('dynamicInner', { read: ViewContainerRef, static: true }) set innerHost(host: ViewContainerRef){
    this._innerHost.next(host);
  }

  _currentStep = new ReplaySubject<Step>();
  currentStep$ = this._currentStep.asObservable();

  transactionGuid: string = Guid.newGuid();
  transactionStarted = new BehaviorSubject<boolean>(false);
  transactionStarted$ = this.transactionStarted.asObservable();

  transactionFinished = new BehaviorSubject<boolean>(false);
  transactionFinished$ = this.transactionStarted.asObservable();

  private _subscriptions: Subscription[] = [];

  constructor(
    private _appState: AppStateService,
    private _ref: MatDialogRef<OfferTransactionDialogComponent>,
    private _componentFactoryResolver: ComponentFactoryResolver,
    private _persistenceService: PersistenceService,
    private _offerService: OfferService,
    private _fileService: FileService,
    private _calendarService: CalendarService,
    private _mailService: MailService,
    @Inject(MAT_DIALOG_DATA) private _data: OfferTransactionDialogComponentInput
  ) { }

  close() {
    if (this.transactionFinished.value) {
      let serverCommand = this.steps[0].serverCommands[0];
      if (serverCommand && serverCommand.successResponse && serverCommand.successResponse) {
        this._ref.close(serverCommand.successResponse);
        return;
      }
    }
    this._ref.close(false);
  }

  executeTransaction() {
    this._persistenceService.executePendingCommands(this.transactionGuid);
  }

  generateDelta() {
    this._persistenceService.clearTransaction(this.transactionGuid);
    for (let step of this.steps) {
      if (typeof (step.generateDelta) === 'function') {
        let commands = step.generateDelta(
          this._appState,
          this._persistenceService,
          this.transactionGuid,
          this._offerService,
          this.formGroup,
          this._fileService,
          this._calendarService,
          this._mailService
        );
        if (commands.length === 0) {
          step.serverCommands = [];
          continue;
        }
        step.serverCommands = commands;
        this._persistenceService.addCommands(commands);
      }
    }
  }

  loadDialogInner(host: ViewContainerRef, step: Step) {
    const viewContainerRef = host;
    viewContainerRef.clear();
    if (step && step.innerView) {
      const componentFactory = this._componentFactoryResolver.resolveComponentFactory(step.innerView);
      const injector = ReflectiveInjector.resolveAndCreate([
        { provide: 'formGroup', useValue: step.formGroup },
        { provide: 'steps', useValue: this.steps },
        { provide: 'offerFormGroup', useValue: this.offerControl }
      ]);
      this.componentRef = viewContainerRef.createComponent<IOfferTransactionView>(componentFactory, 0, injector);
    }
  }

  nextStep() {
    if (this.hasNextStep) {
      this.stepperIndex++;
      this._currentStep.next(this.visibleSteps[this.stepperIndex]);
    }
  }

  ngOnDestroy(): void {
    this.transactionStarted.complete();
    this.transactionFinished.complete();
    this._persistenceService.clear();
    for (let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
  }

  ngOnInit(): void {
    if (this._appState.objectCache.currentOfferTransaction) this.setTransaction(this._appState.objectCache.currentOfferTransaction);
    this._subscriptions.push(...[
      combineLatest([this.innerHost$, this.currentStep$]).subscribe(([host, step]) => this.setStep(host, step)),
      this._persistenceService.procedureStarted$.subscribe(
        (transaction) => {
          transaction === this.transactionGuid ? this.transactionStarted.next(true) : this.transactionStarted.next(false);
        }
      ),
      this._persistenceService.onAllCommandsDone$.subscribe(
        (value) => {
          this.transactionFinished.next(value);
        }
      )
    ]);
  }

  previousStep() {
    if (this.hasPreviousStep) {
      this.stepperIndex--;
      this._currentStep.next(this.visibleSteps[this.stepperIndex]);
    }
  }

  setStep(host, step: Step) {
    if (!step || step.isHidden) return;
    this.loadDialogInner(host, step);
    this.validateStepper();
  }

  setTransaction(transaction: OfferTransaction) {

    this.title = transaction.name;

    var transactionData = OfferTransactionMap[1];

    if (this._data && this._data.patchValue) transactionData.formGroup.patchValue(this._data.patchValue);

    this.formGroup.addControl(transactionData.formGroupName, transactionData.formGroup);
    this.steps = [{
      'title': 'Kopfdaten bearbeiten',
      'formGroup': this.formGroup.controls[transactionData.formGroupName] as FormGroup,
      'isHidden': true,
      'innerView': transactionData.view as any,
      'summaryView': transactionData.summaryView as any
    }];

    for (let attribute of transaction.offertransactionattribute) {
      transactionData = OfferTransactionMap[attribute.id];
      if (!transactionData) {
        console.warn(`cannot find transaction meta data for transaction ${attribute.id} ${attribute.name}`);
        continue;
      } else if (attribute.id === 1) {
        this.steps[0].isHidden = false;
        continue;
      }
      this.formGroup.addControl(transactionData.formGroupName, transactionData.formGroup);
      this.steps.push({
        'title': attribute.name,
        'formGroup': this.formGroup.controls[transactionData.formGroupName] as FormGroup,
        'isHidden': false,
        'innerView': transactionData.view as any,
        'summaryView': transactionData.summaryView as any
      });
    }

    this.steps.push({
      'formGroup': this.formGroup,
      'title': 'Zusammenfassung',
      'innerView': TransactionSummaryFormComponent,
      'isHidden': false
    } as Step);

    this.visibleSteps = this.steps.filter(x => !x.isHidden);

    this._currentStep.next(this.visibleSteps[0]);
  }

  stepperChanged(event: StepperSelectionEvent) {
    this.stepperIndex = event.selectedIndex;
    let step = this.visibleSteps[event.selectedIndex];
    if (step && !step.isHidden) this._currentStep.next(step);
  }

  validateStepper() {
    this.hasNextStep = (this.visibleSteps.length - 1) > this.stepperIndex;
    this.hasPreviousStep = this.stepperIndex > 0;
    if (this.stepperIndex === (this.visibleSteps.length - 1)) this.generateDelta();
  }

  get offerControl(): FormGroup {
    return this.formGroup.controls['offer'] as FormGroup;
  }

}

class Step {
  title: string;
  formGroup: FormGroup;
  innerView: Type<IOfferTransactionView>;
  isHidden: boolean = false;
  summaryView?: Type<IOfferSummaryView>;
  generateDelta?: (
    appState: AppStateService,
    persistenceService: PersistenceService,
    transaction: string,
    offerService: OfferService,
    globalForm: FormGroup,
    fileService: FileService,
    calendarService: CalendarService,
    mailService: MailService
  ) => ServerCommand<any, any>[];
  serverCommands?: ServerCommand<any, any>[];
}

export class OfferTransactionDialogComponentInput {
  patchValue?: Offer;
}